import { useEffect, useState, type FormEvent } from 'react';
import { Page } from '../components/Page.js';
import { webauthnSupported, enrollBiometric } from '../webauthn.js';

type BioCred = { id: number; label: string | null; created_at: string; last_used_at: string | null };

function BiometricSection() {
  const [creds, setCreds] = useState<BioCred[]>([]);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const supported = webauthnSupported();

  const load = () => fetch('/api/auth/webauthn/credentials').then((r) => (r.ok ? r.json() : { credentials: [] })).then((d) => setCreds(d.credentials ?? [])).catch(() => {});
  useEffect(() => { load(); }, []);

  async function add() {
    setErr(null); setMsg(null); setBusy(true);
    try {
      const label = window.prompt('Name this device (e.g. "Lee’s phone"):', 'My phone');
      if (label === null) { setBusy(false); return; }
      await enrollBiometric(label || 'This device');
      setMsg('Biometric unlock is set up on this device.');
      await load();
    } catch (e) { setErr((e as Error).message); }
    finally { setBusy(false); }
  }
  async function remove(id: number) {
    if (!window.confirm('Remove biometric unlock for this device? You can re-add it anytime.')) return;
    await fetch(`/api/auth/webauthn/credentials/${id}`, { method: 'DELETE' });
    await load();
  }

  return (
    <section className="card card-pad" style={{ marginTop: 16 }}>
      <h3 style={{ marginTop: 0, fontSize: 15 }}>Biometric unlock</h3>
      <p className="muted" style={{ fontSize: 13, marginTop: 0 }}>
        Unlock with your phone’s fingerprint or face instead of typing your passphrase. Your passphrase and
        recovery code still work and remain the way to recover your vault. Set this up <b>on each device</b>
        you want to use it on. The unlock key never leaves the device; it’s gated by your biometric.
      </p>
      {creds.length > 0 && (
        <ul style={{ margin: '0 0 10px', paddingLeft: 16, fontSize: 13 }}>
          {creds.map((c) => (
            <li key={c.id} style={{ marginBottom: 4 }}>
              <b>{c.label ?? 'Device'}</b>{' '}
              <span className="muted3" style={{ fontSize: 11.5 }}>added {c.created_at.slice(0, 10)}{c.last_used_at ? ` · last used ${c.last_used_at.slice(0, 10)}` : ''}</span>{' '}
              <button className="btn btn-ghost" style={{ fontSize: 11, padding: '1px 8px' }} onClick={() => remove(c.id)}>Remove</button>
            </li>
          ))}
        </ul>
      )}
      {!supported && <div className="muted3" style={{ fontSize: 12.5, marginBottom: 8 }}>This browser doesn’t support biometric unlock. Try Chrome on Android, or the installed app.</div>}
      <button className="btn btn-primary" type="button" disabled={busy || !supported} onClick={add}>
        {busy ? 'Follow the prompt…' : 'Set up biometric unlock on this device'}
      </button>
      {msg && <div style={{ color: 'var(--ok)', fontSize: 12.5, marginTop: 8 }}>{msg}</div>}
      {err && <div style={{ color: 'var(--bad)', fontSize: 12.5, marginTop: 8 }}>{err}</div>}
    </section>
  );
}

type Settings = { subject_name?: string; annual_renotification_last?: string; no_fixed_abode?: string };
type Anchor = { status: string; bitcoin_block_height: number | null; submitted_at: string } | null;

export function Settings() {
  const [name, setName] = useState('');
  const [annual, setAnnual] = useState('');
  const [noFixedAbode, setNoFixedAbode] = useState(false);
  const [anchor, setAnchor] = useState<Anchor>(null);
  const [provider, setProvider] = useState('');
  const [saved, setSaved] = useState(false);
  const [busy, setBusy] = useState(false);

  async function load() {
    const [s, a] = await Promise.all([
      fetch('/api/settings').then((r) => r.json()).catch(() => ({ settings: {} })),
      fetch('/api/anchors').then((r) => r.json()).catch(() => ({})),
    ]);
    const st: Settings = s.settings ?? {};
    setName(st.subject_name ?? ''); setAnnual(st.annual_renotification_last ?? '');
    setNoFixedAbode(st.no_fixed_abode === '1');
    setAnchor(a.latest ?? null); setProvider(a.provider ?? '');
  }
  useEffect(() => { load(); }, []);

  async function save(e: FormEvent) {
    e.preventDefault(); setBusy(true); setSaved(false);
    try {
      await fetch('/api/settings', { method: 'POST', headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ subject_name: name, annual_renotification_last: annual, no_fixed_abode: noFixedAbode ? '1' : '' }) });
      setSaved(true); setTimeout(() => setSaved(false), 2000);
    } finally { setBusy(false); }
  }

  return (
    <Page eyebrow="Evidence & access" title="Settings">
      <form className="card card-pad" onSubmit={save} style={{ marginBottom: 16 }}>
        <h3 style={{ marginTop: 0, fontSize: 15 }}>Profile & deadlines</h3>
        <label className="fld"><span>Your full legal name (shown on the device-notification PDF)</span>
          <input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Lee John Ashcroft" />
        </label>
        <label className="fld" style={{ maxWidth: 280 }}><span>Last annual re-notification date</span>
          <input type="date" value={annual} max={new Date().toISOString().slice(0, 10)} onChange={(e) => setAnnual(e.target.value)} />
        </label>
        <p className="muted" style={{ fontSize: 12.5, marginTop: 0 }}>
          This drives the periodic (R1) reminder — the next one is due 12 months after this date. Update it
          whenever you complete your re-notification (recording an R1 notification does this for you).
        </p>
        <label className="muted" style={{ fontSize: 12.5, display: 'flex', gap: 8, alignItems: 'flex-start', margin: '4px 0 8px' }}>
          <input type="checkbox" checked={noFixedAbode} onChange={(e) => setNoFixedAbode(e.target.checked)} style={{ marginTop: 2 }} />
          <span>I currently have <b>no sole or main residence</b> (no fixed abode). While this is set, the
            re-notification reminder switches from yearly to <b>every 7 days</b> (reg 9). Untick it once you
            have a home address again.</span>
        </label>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
          <button className="btn btn-primary" type="submit" disabled={busy}>{busy ? 'Saving…' : 'Save settings'}</button>
          {saved && <span className="muted" style={{ fontSize: 13, color: 'var(--ok)' }}>Saved ✓</span>}
        </div>
      </form>

      <section className="card card-pad" style={{ marginBottom: 16 }}>
        <h3 style={{ marginTop: 0, fontSize: 15 }}>Status</h3>
        <div className="muted" style={{ fontSize: 13, lineHeight: 1.8 }}>
          <div>Bitcoin anchoring provider: <b>{provider || '—'}</b></div>
          <div>Latest anchor: {anchor
            ? (anchor.status === 'confirmed' ? <b>confirmed · Bitcoin block {anchor.bitcoin_block_height}</b> : <b>pending Bitcoin confirmation</b>)
            : <b>none yet</b>}</div>
          <div className="muted3" style={{ fontSize: 12 }}>Pending anchors confirm automatically within a few hours (hourly check) — see Verify.</div>
        </div>
      </section>

      <section className="card card-pad">
        <h3 style={{ marginTop: 0, fontSize: 15 }}>Vault</h3>
        <p className="muted" style={{ fontSize: 13, marginTop: 0 }}>
          Locking clears your decryption key from memory; you’ll need your passphrase to unlock again.
          There is no passphrase backdoor — only your sealed recovery code can restore access if it’s lost.
        </p>
        <button className="btn btn-ghost" type="button"
          onClick={() => fetch('/api/auth/lock', { method: 'POST' }).then(() => window.location.reload())}>
          Lock vault now
        </button>
      </section>

      <section className="card card-pad" style={{ marginTop: 16 }}>
        <h3 style={{ marginTop: 0, fontSize: 15 }}>Install on your phone</h3>
        <p className="muted" style={{ fontSize: 13, marginTop: 0 }}>
          SOR can run as an app on your phone. Two ways:
        </p>
        <ul className="muted" style={{ fontSize: 13, lineHeight: 1.7, marginTop: 0 }}>
          <li><b>Install from the browser (recommended):</b> open this site on your phone, then your browser
            menu → <b>Add to Home screen / Install app</b>. Updates automatically.</li>
          <li><b>Android app (.apk):</b> on your phone, open <span className="mono">vault.blackpoolweb.co.uk/download/sor.apk</span>{' '}
            and install it (you may need to allow installing from your browser once).</li>
        </ul>
        <a className="btn btn-ghost" href="/download/sor.apk" download>Download Android app (.apk)</a>
        <p className="muted3" style={{ fontSize: 11.5, marginTop: 8, marginBottom: 0 }}>
          The app is a secure shell around this site — your records stay in the encrypted vault on the
          server; nothing sensitive is stored on the phone. The camera works for device photos either way.
        </p>
      </section>

      <BiometricSection />
    </Page>
  );
}
